import api from "./api";
import { ok, getMe, can, ORG_SCOPES, listRecords, listCyclesCached, parseUtc, isoUtc, nameOf, pMap, rememberName } from "./core";
import { REMINDER_TASK_TYPES } from "../utils/constants";

const STAGE_TASK = {
  GOAL_SETTING: REMINDER_TASK_TYPES.GOAL_SETTING,
  GOAL_APPROVAL: REMINDER_TASK_TYPES.GOAL_APPROVAL,
  SELF_ASSESSMENT: REMINDER_TASK_TYPES.SELF_ASSESSMENT,
  TRACKING: REMINDER_TASK_TYPES.SELF_ASSESSMENT,
  MANAGER_ASSESSMENT: REMINDER_TASK_TYPES.MANAGER_REVIEW,
  HR_FINALIZATION: "HR finalization pending",
};
const TASK_STAGE = {
  [REMINDER_TASK_TYPES.GOAL_SETTING]: "GOAL_SETTING",
  [REMINDER_TASK_TYPES.GOAL_APPROVAL]: "GOAL_APPROVAL",
  [REMINDER_TASK_TYPES.SELF_ASSESSMENT]: "SELF_ASSESSMENT",
  [REMINDER_TASK_TYPES.MANAGER_REVIEW]: "MANAGER_ASSESSMENT",
  "HR finalization pending": "HR_FINALIZATION",
};
const LINK = {
  GOAL_SETTING: "/my-goals", GOAL_APPROVAL: "/team/goal-approvals", SELF_ASSESSMENT: "/self-assessment",
  TRACKING: "/self-assessment", MANAGER_ASSESSMENT: "/team/assessments", HR_FINALIZATION: "/hr/review",
};

const SENT_KEY = "pms.reminders.sent";
const readSent = () => {
  try { return JSON.parse(localStorage.getItem(SENT_KEY) || "{}"); } catch { return {}; }
};
function rememberSent(recordId, stage) {
  try {
    const all = readSent();
    const key = `${recordId}|${stage || ""}`;
    const prev = all[key] || { count: 0 };
    all[key] = { at: new Date().toISOString(), count: prev.count + 1 };
    localStorage.setItem(SENT_KEY, JSON.stringify(all));
  } catch {
  }
}
async function auditReminders() {
  const me = await getMe();
  if (!can(me, "audit.security.read")) return {};
  try {
    const { data } = await api.get("/audit-logs", { params: { event_type: "REMINDER_REQUESTED", limit: 200 } });
    const out = {};
    data.items.forEach((r) => {
      const key = String(r.resource_id);
      const at = isoUtc(r.created_at);
      out[key] = { at: !out[key] || at > out[key].at ? at : out[key].at, count: (out[key]?.count || 0) + 1 };
    });
    return out;
  } catch {
    return {};
  }
}

export async function getPendingTasks(cycleId) {
  const [{ data }, records, fromAudit] = await Promise.all([
    api.get("/reports/pending-actions", { params: { cycle_id: cycleId, limit: 500 } }),
    listRecords(cycleId),
    auditReminders(),
  ]);
  const local = readSent();
  const byEmployee = Object.fromEntries(records.map((r) => [r.employee_id, r]));
  const today = Date.now();
  return ok(data.rows.map((row) => {
    rememberName(row.employee_id, row.employee_name);
    const record = byEmployee[row.employee_id];
    const due = parseUtc(row.due_date);
    const overdue = due ? Math.floor((today - due.getTime()) / 86400000) : 0;
    const ownerIsEmployee = row.owner === row.employee_id;
    return {
      id: record ? String(record.performance_record_id) : `${row.employee_id}-${row.stage}`,
      recordId: record?.performance_record_id,
      employeeId: row.employee_id,
      employeeName: row.employee_name,
      designationName: "",
      departmentName: "",
      managerName: nameOf(record?.review_manager_employee_id),
      ownerId: row.owner,
      ownerName: nameOf(row.owner),
      taskType: STAGE_TASK[row.stage] || row.stage,
      detail: ownerIsEmployee ? `Waiting on ${row.employee_name}` : `Waiting on ${nameOf(row.owner)} for ${row.employee_name}`,
      dueDate: isoUtc(row.due_date),
      daysOverdue: overdue > 0 ? overdue : 0,
      ...(() => {
        const mine = record ? (local[`${record.performance_record_id}|${row.stage}`] || local[`${record.performance_record_id}|`]) : null;
        const audit = record ? fromAudit[String(record.performance_record_id)] : null;
        const at = [mine?.at, audit?.at].filter(Boolean).sort().pop() || null;
        return { lastRemindedAt: at, reminderCount: Math.max(mine?.count || 0, audit?.count || 0) };
      })(),
      stage: row.stage,
      link: LINK[row.stage] || "/dashboard",
    };
  }));
}

export async function sendReminders(payload = {}) {
  const me = await getMe();
  if (!can(me, "reminder.manage", ORG_SCOPES)) {
    throw new Error("Only HR can send reminders from the PMS right now. Ask HR, or set up an automatic reminder rule.");
  }
  let recordIds = (payload.taskIds || []).map(Number).filter(Boolean);
  if (!recordIds.length && payload.employeeIds?.length) {
    const cycles = await listCyclesCached();
    const cycleId = payload.cycleId || cycles.find((c) => c.status === "Active")?.id;
    const records = await listRecords(cycleId);
    recordIds = records.filter((r) => payload.employeeIds.includes(r.employee_id)).map((r) => r.performance_record_id);
  }
  const reason = String(payload.message || "").trim().slice(0, 1000) || "Reminder";
  const stageOf = Object.fromEntries((payload.tasks || []).map((t) => [String(t.recordId), t.stage]));
  const results = await pMap(recordIds, async (id) => {
    try {
      await api.post(`/records/${id}/remind`, { reason });
      rememberSent(id, stageOf[String(id)]);
      return true;
    } catch {
      return false;
    }
  }, 4);
  const sent = results.filter(Boolean).length;
  if (!sent) throw new Error("No reminder was sent. The task may already be complete or outside its stage window.");
  return ok({ sent, skipped: results.length - sent });
}

const RULE_STAGES = ["GOAL_SETTING", "GOAL_APPROVAL", "SELF_ASSESSMENT", "MANAGER_ASSESSMENT", "HR_FINALIZATION"];
const FREQUENCY_DAYS = { Daily: 1, "Every 2 days": 2, Weekly: 7, Once: 365 };
const daysToFrequency = (d) => Object.entries(FREQUENCY_DAYS).find(([, v]) => v === d)?.[0] || `Every ${d} days`;
const frequencyToDays = (f) => FREQUENCY_DAYS[f] || Number(/^Every (\d+) days$/.exec(f || "")?.[1]) || 2;

export async function getReminderRules(cycleId) {
  const cycles = await listCyclesCached();
  const cycle = cycles.find((c) => c.id === String(cycleId)) || cycles.find((c) => c.status === "Active");
  const { data } = await api.get("/reminder-rules");
  const forCycle = data.filter((r) => !cycle || r.cycle_id === cycle.cycleId);
  return ok(RULE_STAGES.map((stage) => {
    const rule = forCycle.filter((r) => r.stage_type === stage && r.is_active).sort((a, b) => b.reminder_rule_id - a.reminder_rule_id)[0];
    return {
      id: rule ? rule.reminder_rule_id : `new-${stage}`,
      ruleId: rule?.reminder_rule_id || null,
      cycleId: cycle?.cycleId,
      stage,
      taskType: STAGE_TASK[stage],
      enabled: Boolean(rule),
      frequency: rule ? daysToFrequency(rule.repeat_days) : "Every 2 days",
      daysBeforeDue: rule ? rule.days_before : 3,
      channels: ["In-app"],
      escalateToManager: Boolean(rule?.escalation_employee_id),
      escalateAfterDays: rule?.escalate_after_days || null,
      escalationEmployeeId: rule?.escalation_employee_id || null,
    };
  }));
}

export async function updateReminderRule(ruleId, patch = {}, current = {}) {
  const rule = { ...current, ...patch };
  if (current.ruleId) {
    await api.post(`/reminder-rules/${current.ruleId}/disable`, { reason: patch.enabled === false ? "Rule switched off" : "Rule replaced with new settings" });
  }
  if (rule.enabled === false) return ok({ disabled: true });
  const stage = rule.stage || TASK_STAGE[rule.taskType];
  const body = {
    cycle_id: Number(rule.cycleId),
    stage_type: stage,
    days_before: Number(rule.daysBeforeDue ?? 3),
    repeat_days: frequencyToDays(rule.frequency),
    reason: "Reminder rule saved",
  };
  if (rule.escalationEmployeeId && rule.escalateAfterDays) {
    body.escalate_after_days = Number(rule.escalateAfterDays);
    body.escalation_employee_id = rule.escalationEmployeeId;
  }
  const { data } = await api.post("/reminder-rules", body);
  return ok(data);
}

export const dispatchReminders = (reason = "Reminders dispatched manually") => api.post("/reminders/dispatch", { reason });

export async function getReminderLog({ cycleId } = {}) {
  const me = await getMe();
  if (!can(me, "audit.security.read")) return ok([]);
  try {
    const { data } = await api.get("/audit-logs", { params: { event_type: "REMINDER_REQUESTED", limit: 100 } });
    const records = cycleId ? await listRecords(cycleId).catch(() => []) : [];
    const who = (recordId) => {
      const record = records.find((x) => String(x.performance_record_id) === String(recordId));
      return record ? nameOf(record.employee_id) : `Record ${recordId}`;
    };
    return ok(data.items.map((r) => ({
      id: r.activity_id,
      sentAt: isoUtc(r.created_at),
      sentBy: nameOf(r.actor_employee_id),
      taskType: "Manual reminder",
      recipients: [who(r.resource_id)],
      channels: ["In-app"],
      message: r.reason || "",
    })));
  } catch {
    return ok([]);
  }
}
